import type { ImageLoader } from 'next/image';
import packageJson from './../../../package.json';

type Props = Parameters<ImageLoader>[0] & {
	height?: number;
};

function isLocalImage(src: string): boolean {
	return (
		src.startsWith('/_next/static/media') ||
		src.startsWith('/images/') ||
		src.startsWith('/generated/terms') ||
		src.startsWith('/files/')
	);
}

const getRandomString = (length: number): string => {
	const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
	let result = '';
	for (let i = 0; i < length; i++) {
		result += characters.charAt(Math.floor(Math.random() * characters.length));
	}
	return result;
};

const TESTING_ONLY_DEPLOYMENT_ID = getRandomString(8);

/**
 * Prepares parameters for image loading with width and quality
 */
function prepareParams(width: Props['width'], quality: Props['quality'], height: Props['height']) {
	return {
		width: width.toString(),
		quality: (quality || 85).toString(),
		height: height ? height.toString() : undefined,
	};
}

function getVersionTag(): string {
	const { version } = packageJson;

	if (process.env.NEXT_PUBLIC_TARGET_ENV === 'prod') {
		return version;
	}

	return `${version}-${TESTING_ONLY_DEPLOYMENT_ID}`;
}

/**
 * Cloudfront image loader for Next.js Image component
 */
export default function cloudfrontLoader({ src, width, quality, height }: Props): string {
	const params = prepareParams(width, quality, height);

	if (process.env.NODE_ENV === 'development') {
		return `${src}?w=${params.width}&q=${params.quality}`;
	}

	const url = new URL(process.env.NEXT_PUBLIC_IMAGE_CDN_URL);
	url.searchParams.set('url', src);
	url.searchParams.set('format', 'webp');
	url.searchParams.set('width', params.width);
	if (params.height) {
		url.searchParams.set('height', params.height);
	}
	url.searchParams.set('quality', params.quality);
	url.searchParams.set('version', getVersionTag());

	if (isLocalImage(src)) {
		url.searchParams.set('domain', process.env.NEXT_PUBLIC_BASE_URL);
	}

	return url.href;
}
